// Overspeeding & Green Driving Events Report
// ---------------------------------------------------------------------------
// Lab-grade comparison tool. Loads AssetPro's daily all-device-report for a
// filter set, aggregates the per-asset-per-day rows into one row per asset,
// and displays the existing overspeed / green-driving counts. Each row
// exposes a "Recalculate" button that re-fetches raw IoT telemetry for the
// same window and posts it to the local Python `overspeed-service` so the
// new stage-based algorithm's output can be compared side-by-side.
//
// Filter bar patterns are cloned from IoTSummaryReport.js. The data fetcher
// (`fetchDailyFuelDataReport`) and per-device raw fetch
// (`fetchIotDeviceDataInPeriod`) are reused from src/utils/fuelService.js
// unchanged.
//
// IMPORTANT: the daily-fuel-data all-device-report returns records with a
// deeply-nested asset sub-object (see transformApiResponseToCsvFormat in
// IoTSummaryReport.js for the canonical shape). We mirror that normalization
// here rather than using flat key guesses.

import React, { useState, useMemo, useCallback } from 'react';
import {
  Layout, Card, Row, Col, Button, Select, DatePicker, InputNumber,
  Table, Tag, Tooltip, Alert, message, Modal, Badge, Typography, Icon, Divider,
} from 'antd';
import moment from 'moment';
import { fetchDailyFuelDataReport } from './utils/fuelService';
import { recomputeForAsset } from './utils/overspeedRecomputeService';
import OverspeedEventMapModal from './OverspeedEventMapModal';

const { Content } = Layout;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Some AssetPro fields come back as either a string or `{name, code}` — accept both.
const nestedName = (v) => {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'object') return v.name || v.code || '';
  return String(v);
};

const toNum = (v) => {
  if (v === undefined || v === null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

// The daily all-device-report carries safety data under two nested objects
// (confirmed from the ops-hub `fuelDataService.js` port):
//
//   row.overspeed_summary = {
//     total_overspeed_events: number,
//     overspeed_events:       [{ start_timestamp, end_timestamp, peak_speed, ... }, ...]
//   }
//
//   row.green_driving_summary = {
//     counts: { "1": n, "2": n, "3": n },     // 1=accel 2=brake 3=corner
//     logs:   { "1": [...], "2": [...], ... } // events: { timestamp, lat_long, value }
//   }
//
// `row.asset.speedLimit` carries the per-asset policy speed limit (kmph).

const HARSH_TYPE_LABEL = {
  1: 'Harsh Acceleration',
  2: 'Harsh Braking',
  3: 'Harsh Cornering',
};

const extractBehaviour = (record) => {
  const os = record.overspeed_summary || {};
  const gd = record.green_driving_summary || {};
  const counts = gd.counts || {};
  const logs   = gd.logs   || {};

  // Overspeed events come as an array of event objects.
  const overspeedEvents = Array.isArray(os.overspeed_events) ? os.overspeed_events : [];
  const overspeedCount  = toNum(os.total_overspeed_events)
    ?? (overspeedEvents.length || undefined);

  // Harsh — map numeric keys (1,2,3) to accel/brake/corner buckets.
  const bucketOf = (k) => ({ 1: 'accel', 2: 'brake', 3: 'corner' }[String(k)] || null);

  const out = {
    overspeed: { count: overspeedCount, events: overspeedEvents },
    accel:     { count: undefined,      events: [] },
    brake:     { count: undefined,      events: [] },
    corner:    { count: undefined,      events: [] },
  };
  for (const [k, v] of Object.entries(counts)) {
    const bucket = bucketOf(k);
    if (!bucket) continue;
    const n = toNum(v);
    if (n !== undefined) out[bucket].count = (out[bucket].count || 0) + n;
  }
  for (const [k, arr] of Object.entries(logs)) {
    const bucket = bucketOf(k);
    if (!bucket || !Array.isArray(arr)) continue;
    // Normalize each event with a type tag so the shared events-table
    // renderer can label rows.
    for (const ev of arr) {
      out[bucket].events.push({
        type: k,
        typeLabel: HARSH_TYPE_LABEL[k] || `Type ${k}`,
        timestamp: ev?.timestamp,
        lat_long:  ev?.lat_long,
        value:     ev?.value,
        _raw: ev,
      });
    }
    // Harsh counts may come only from logs on some days — fall back to
    // log length if the `counts` bucket was missing.
    if (out[bucket].count === undefined) out[bucket].count = arr.length;
  }
  return out;
};

// Normalize one daily record to a flat shape we can display / filter on.
const normalizeDaily = (rec) => {
  const asset = rec.asset || {};
  const eqSubType = asset.equipmentSubType || {};
  const assetTypeObj = (eqSubType && typeof eqSubType === 'object' && eqSubType.assetType) || asset.assetType;
  const behaviour = extractBehaviour(rec);

  return {
    assetId:        asset._id || asset.id || null,
    assetCode:      asset.assetCode || '',
    ownerCode:      asset.ownerCode || '',
    altName:        asset.altName || '',
    // deviceId sits on the daily record itself (see fuelService.getDeviceIdFromAsset fallback).
    deviceId:       rec.device_id || rec.deviceId || asset.tagDeviceId || null,
    assetType:      nestedName(assetTypeObj),
    assetSubType:   nestedName(eqSubType),
    assetModel:     nestedName(asset.assetModel),
    location:       nestedName(asset.location),
    subLocation:    nestedName(asset.subLocation),
    speedLimit:     toNum(asset.speedLimit),
    dateTimestamp:  rec.date_timestamp || rec.date || null,
    // Driving-behaviour counters and event arrays.
    existingOverspeed:         behaviour.overspeed.count,
    existingOverspeedEvents:   behaviour.overspeed.events,
    existingHarshAccel:        behaviour.accel.count,
    existingHarshAccelEvents:  behaviour.accel.events,
    existingHarshBrake:        behaviour.brake.count,
    existingHarshBrakeEvents:  behaviour.brake.events,
    existingHarshCorner:       behaviour.corner.count,
    existingHarshCornerEvents: behaviour.corner.events,
    _raw: rec,
  };
};

// Aggregate per-asset-per-day normalized records into one row per asset by
// summing the counter fields across the date range. Asset metadata is taken
// from the most recent record (latest date_timestamp).
const aggregateByAsset = (daily) => {
  const byAsset = new Map();
  const sum = (a, b) => {
    if (a === undefined && b === undefined) return undefined;
    return (a || 0) + (b || 0);
  };
  const tagEvents = (row, events, kind) => (events || []).map((e) => ({
    ...e,
    _date: row.dateTimestamp,
    _kind: kind,
  }));

  for (const row of daily) {
    const key = row.assetId || row.assetCode || `${row.altName}-${row.deviceId}` || JSON.stringify(row._raw).slice(0, 40);
    const existing = byAsset.get(key);
    if (!existing) {
      byAsset.set(key, {
        ...row,
        existingOverspeedEvents:    tagEvents(row, row.existingOverspeedEvents,    'overspeed'),
        existingHarshAccelEvents:   tagEvents(row, row.existingHarshAccelEvents,   'accel'),
        existingHarshBrakeEvents:   tagEvents(row, row.existingHarshBrakeEvents,   'brake'),
        existingHarshCornerEvents:  tagEvents(row, row.existingHarshCornerEvents,  'corner'),
        dailyRecords: [row],
      });
    } else {
      existing.dailyRecords.push(row);
      if ((row.dateTimestamp || 0) >= (existing.dateTimestamp || 0)) {
        existing.assetType    = row.assetType    || existing.assetType;
        existing.assetSubType = row.assetSubType || existing.assetSubType;
        existing.assetModel   = row.assetModel   || existing.assetModel;
        existing.location     = row.location     || existing.location;
        existing.subLocation  = row.subLocation  || existing.subLocation;
        existing.deviceId     = existing.deviceId || row.deviceId;
        existing.dateTimestamp = row.dateTimestamp;
      }
      existing.existingOverspeed   = sum(existing.existingOverspeed,   row.existingOverspeed);
      existing.existingHarshAccel  = sum(existing.existingHarshAccel,  row.existingHarshAccel);
      existing.existingHarshBrake  = sum(existing.existingHarshBrake,  row.existingHarshBrake);
      existing.existingHarshCorner = sum(existing.existingHarshCorner, row.existingHarshCorner);
      existing.existingOverspeedEvents.push(...tagEvents(row, row.existingOverspeedEvents, 'overspeed'));
      existing.existingHarshAccelEvents.push(...tagEvents(row, row.existingHarshAccelEvents, 'accel'));
      existing.existingHarshBrakeEvents.push(...tagEvents(row, row.existingHarshBrakeEvents, 'brake'));
      existing.existingHarshCornerEvents.push(...tagEvents(row, row.existingHarshCornerEvents, 'corner'));
    }
  }
  return Array.from(byAsset.values());
};

const rowKeyFor = (r) => r.assetId || r.assetCode || r.deviceId || r.altName;

// Pretty-print stage_counts on the table in a single compact chip line
const renderStageCounts = (stageCounts) => {
  if (!stageCounts) return null;
  const order = [
    'raw_rows',
    'raw_overspeed',
    'overspeed_after_quality',
    'overspeed_after_haversine',
    'overspeed_after_accel',
    'overspeed_after_accelerometer',
    'overspeed_after_teleport',
    'overspeed_after_local_traj',
    'overspeed_after_median',
  ];
  const parts = order
    .filter((k) => stageCounts[k] !== undefined)
    .map((k) => `${k.replace('overspeed_after_', '').replace(/_/g, ' ')}:${stageCounts[k]}`);
  return <Text style={{ fontSize: 11, color: '#555' }}>{parts.join(' → ')}</Text>;
};

const uniq = (vals) => Array.from(new Set(vals.filter((v) => v !== undefined && v !== null && v !== ''))).sort();

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const OverspeedGreenDrivingReport = () => {
  // Filter bar state
  const [dateRange, setDateRange] = useState([
    moment().subtract(1, 'day').startOf('day'),
    moment().subtract(1, 'day').endOf('day'),
  ]);
  const [speedLimitKmph, setSpeedLimitKmph] = useState(30);
  const [peakMarginPct, setPeakMarginPct] = useState(10);

  const [selectedAssetCode, setSelectedAssetCode] = useState(null);
  const [selectedAssetType, setSelectedAssetType] = useState(null);
  const [selectedAssetSubType, setSelectedAssetSubType] = useState(null);
  const [selectedAssetModel, setSelectedAssetModel] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedSubLocation, setSelectedSubLocation] = useState(null);

  // Loaded data + transient UI state
  const [assetRows, setAssetRows] = useState([]);             // one row per asset, aggregated
  const [loadedRange, setLoadedRange] = useState(null);       // [startTs, endTs] for the data currently displayed
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [loadInfo, setLoadInfo] = useState(null);             // info banner under header
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [recomputeState, setRecomputeState] = useState({});   // { [rowKey]: { status, result, error, rowCount, rows } }
  const [modal, setModal] = useState({ open: false, title: '', content: null });
  const [mapModal, setMapModal] = useState({ open: false, rows: [], event: null, speedLimit: null });

  // Distinct filter options derived from rows ------------------------------
  const assetCodes    = useMemo(() => uniq(assetRows.map((r) => r.assetCode)),    [assetRows]);
  const assetTypes    = useMemo(() => uniq(assetRows.map((r) => r.assetType)),    [assetRows]);
  const assetSubTypes = useMemo(() => uniq(assetRows.map((r) => r.assetSubType)), [assetRows]);
  const assetModels   = useMemo(() => uniq(assetRows.map((r) => r.assetModel)),   [assetRows]);
  const locations     = useMemo(() => uniq(assetRows.map((r) => r.location)),     [assetRows]);
  const subLocations  = useMemo(() => uniq(assetRows.map((r) => r.subLocation)),  [assetRows]);

  // Client-side filtered view ---------------------------------------------
  const filteredRows = useMemo(() => assetRows.filter((r) => {
    if (selectedAssetCode    && r.assetCode    !== selectedAssetCode)    return false;
    if (selectedAssetType    && r.assetType    !== selectedAssetType)    return false;
    if (selectedAssetSubType && r.assetSubType !== selectedAssetSubType) return false;
    if (selectedAssetModel   && r.assetModel   !== selectedAssetModel)   return false;
    if (selectedLocation     && r.location     !== selectedLocation)     return false;
    if (selectedSubLocation  && r.subLocation  !== selectedSubLocation)  return false;
    return true;
  }), [
    assetRows, selectedAssetCode, selectedAssetType, selectedAssetSubType,
    selectedAssetModel, selectedLocation, selectedSubLocation,
  ]);

  // ---- Load report -------------------------------------------------------
  const handleLoad = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.warning('Select a date range first');
      return;
    }
    const startTs = dateRange[0].clone().startOf('day').valueOf();
    const endTs   = dateRange[1].clone().endOf('day').valueOf();

    setLoading(true);
    setLoadError(null);
    setLoadInfo(null);
    setRecomputeState({});
    setSelectedRowKeys([]);

    try {
      const result = await fetchDailyFuelDataReport(startTs, endTs, {
        assets: null,
        locations: ['all'],
        subLocations: ['all'],
        assetTypes: [],
        assetSubTypes: [],
        assetModels: [],
        frequency: 'Daily',
      });

      if (!result.success) {
        setAssetRows([]);
        setLoadError(result.error || 'Failed to load report');
        return;
      }

      const data = Array.isArray(result.data) ? result.data : [];
      if (data.length > 0) {
        // eslint-disable-next-line no-console
        console.log('[OverspeedGreenDrivingReport] first raw record keys:', Object.keys(data[0]));
        // eslint-disable-next-line no-console
        console.log('[OverspeedGreenDrivingReport] first raw record asset keys:', data[0].asset ? Object.keys(data[0].asset) : '(no asset sub-object)');
        // eslint-disable-next-line no-console
        console.log('[OverspeedGreenDrivingReport] first raw record sample:', data[0]);
      }

      const normalized = data.map(normalizeDaily);
      const aggregated = aggregateByAsset(normalized);

      const totalOverspeed = aggregated.reduce((a, r) => a + (r.existingOverspeed || 0), 0);
      const totalHarsh = aggregated.reduce((a, r) => (
        a + (r.existingHarshAccel || 0) + (r.existingHarshBrake || 0) + (r.existingHarshCorner || 0)
      ), 0);

      setAssetRows(aggregated);
      setLoadedRange([startTs, endTs]);
      setLoadInfo(
        `Loaded ${data.length} daily record${data.length === 1 ? '' : 's'} for `
        + `${aggregated.length} asset${aggregated.length === 1 ? '' : 's'} `
        + `from ${moment(startTs).format('DD MMM YYYY')} to ${moment(endTs).format('DD MMM YYYY')}. `
        + `Existing overspeed events: ${totalOverspeed}. Existing harsh-driving events: ${totalHarsh}.`
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[OverspeedGreenDrivingReport] load failed:', err);
      setLoadError(err.message || String(err));
      setAssetRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedAssetCode(null);
    setSelectedAssetType(null);
    setSelectedAssetSubType(null);
    setSelectedAssetModel(null);
    setSelectedLocation(null);
    setSelectedSubLocation(null);
  };

  // ---- Recalculate a single row ----------------------------------------
  const runRecompute = useCallback(async (row) => {
    const key = rowKeyFor(row);
    if (!row.deviceId || !row.assetId) {
      setRecomputeState((prev) => ({
        ...prev,
        [key]: { status: 'error', error: 'Row missing device id or asset id' },
      }));
      return;
    }
    if (!loadedRange) {
      setRecomputeState((prev) => ({
        ...prev,
        [key]: { status: 'error', error: 'Load the report first' },
      }));
      return;
    }

    setRecomputeState((prev) => ({ ...prev, [key]: { status: 'running' } }));

    const res = await recomputeForAsset({
      deviceId: String(row.deviceId),
      assetId:  String(row.assetId),
      startTs: loadedRange[0],
      endTs:   loadedRange[1],
      speedLimitKmph: Number(speedLimitKmph) || 30,
      peakMarginPct:  Number(peakMarginPct),
    });

    setRecomputeState((prev) => ({
      ...prev,
      [key]: res.success
        ? { status: 'done', result: res.data, rowCount: res.rowCount, rows: res.rows || [] }
        : { status: 'error', error: res.error, rowCount: res.rowCount, fetchErrors: res.fetchErrors, rows: res.rows || [] },
    }));
  }, [loadedRange, speedLimitKmph, peakMarginPct]);

  // ---- Bulk recompute ---------------------------------------------------
  const runBulkRecompute = async () => {
    const targets = filteredRows.filter((r) => selectedRowKeys.includes(rowKeyFor(r)));
    if (targets.length === 0) return;
    // eslint-disable-next-line no-restricted-syntax
    for (const row of targets) {
      // eslint-disable-next-line no-await-in-loop
      await runRecompute(row);
    }
    message.success(`Recomputed ${targets.length} asset${targets.length === 1 ? '' : 's'}`);
  };

  // ---- Modal helpers ----------------------------------------------------
  const playEventOnMap = (event, rawRows) => {
    // Close the events list modal so the map is front and centre.
    setModal({ open: false, title: '', content: null });
    setMapModal({
      open: true,
      rows: rawRows || [],
      event,
      speedLimit: Number(speedLimitKmph) || 30,
    });
  };

  const showEventsModal = (row, result, rawRows) => {
    setModal({
      open: true,
      title: `New Overspeed Events — ${row.assetCode || row.altName || row.deviceId}`,
      content: (
        <Table
          size="small"
          rowKey={(e, i) => `${e.start_ts_ms || i}`}
          pagination={false}
          dataSource={result?.events || []}
          columns={[
            { title: 'Start (IST)', dataIndex: 'start_ist' },
            { title: 'End (IST)',   dataIndex: 'end_ist' },
            { title: 'Duration (s)', dataIndex: 'duration_sec', render: (v) => (v != null ? Number(v).toFixed(1) : '-') },
            { title: 'Peak kmph',   dataIndex: 'peak_speed_kmph', render: (v) => (v != null ? Number(v).toFixed(1) : '-') },
            { title: 'Threshold',   dataIndex: 'peak_threshold', render: (v) => (v != null ? Number(v).toFixed(1) : '-') },
            { title: 'Peak location', render: (_, e) => (e.peak_lat != null ? `${Number(e.peak_lat).toFixed(5)}, ${Number(e.peak_lon).toFixed(5)}` : '-') },
            {
              title: 'Action',
              key: 'mapPlay',
              render: (_, e) => (
                <Tooltip title={rawRows && rawRows.length ? 'Replay this event on a map' : 'No cached raw telemetry — re-run Recalculate'}>
                  <Button
                    size="small"
                    icon="environment"
                    disabled={!rawRows || rawRows.length === 0}
                    onClick={() => playEventOnMap(e, rawRows)}
                  >
                    Play on map
                  </Button>
                </Tooltip>
              ),
            },
          ]}
        />
      ),
    });
  };

  const showExistingEventsModal = (row, kind) => {
    const titleMap = { overspeed: 'Overspeed', accel: 'Harsh Acceleration', brake: 'Harsh Braking', corner: 'Harsh Cornering' };
    const eventsKey = {
      overspeed: 'existingOverspeedEvents',
      accel:     'existingHarshAccelEvents',
      brake:     'existingHarshBrakeEvents',
      corner:    'existingHarshCornerEvents',
    }[kind];
    const events = row[eventsKey] || [];
    const perDayKey = {
      overspeed: 'existingOverspeed',
      accel:     'existingHarshAccel',
      brake:     'existingHarshBrake',
      corner:    'existingHarshCorner',
    }[kind];

    // Per-day breakdown from dailyRecords
    const perDay = (row.dailyRecords || []).map((d) => ({
      date: d.dateTimestamp ? moment(d.dateTimestamp).format('DD MMM YYYY') : '-',
      count: d[perDayKey] ?? 0,
    })).filter((d) => d.count > 0);

    const fmtTs = (ms) => {
      const n = Number(ms);
      if (!Number.isFinite(n) || n <= 0) return '-';
      return moment(n).format('DD MMM YYYY HH:mm:ss');
    };

    // Columns tuned to the two known event shapes (overspeed_events and
    // green_driving_summary.logs). Falls back to generic key/value rendering
    // for unexpected fields.
    const eventColumns = kind === 'overspeed'
      ? [
          { title: 'Day',   render: (_, e) => (e._date ? moment(e._date).format('DD MMM YYYY') : '-') },
          { title: 'Start', render: (_, e) => fmtTs(e.start_timestamp || e.startTimestamp) },
          { title: 'End',   render: (_, e) => fmtTs(e.end_timestamp   || e.endTimestamp) },
          { title: 'Duration (s)', render: (_, e) => {
              const s = Number(e.start_timestamp || e.startTimestamp);
              const end = Number(e.end_timestamp || e.endTimestamp);
              return Number.isFinite(s) && Number.isFinite(end) ? ((end - s) / 1000).toFixed(1) : '-';
            } },
          { title: 'Peak kmph', render: (_, e) => (e.peak_speed ?? e.peakSpeed ?? e.max_speed ?? '-') },
          { title: 'Location',  render: (_, e) => (e.lat_long || e.location || '-') },
        ]
      : [
          { title: 'Day',       render: (_, e) => (e._date ? moment(e._date).format('DD MMM YYYY') : '-') },
          { title: 'Type',      dataIndex: 'typeLabel' },
          { title: 'Timestamp', render: (_, e) => fmtTs(e.timestamp) },
          { title: 'Location',  render: (_, e) => (e.lat_long || '-') },
          { title: 'Value',     dataIndex: 'value' },
        ];

    setModal({
      open: true,
      title: `Existing ${titleMap[kind]} Events — ${row.assetCode || row.altName || row.deviceId}`,
      content: (
        <div>
          {perDay.length > 0 && (
            <>
              <Text strong>Per-day breakdown</Text>
              <Table
                size="small"
                rowKey={(d, i) => `d${i}`}
                pagination={false}
                style={{ marginTop: 8, marginBottom: 16 }}
                dataSource={perDay}
                columns={[
                  { title: 'Date', dataIndex: 'date' },
                  { title: 'Count', dataIndex: 'count' },
                ]}
              />
            </>
          )}
          {events.length > 0 ? (
            <>
              <Text strong>Event details ({events.length})</Text>
              <Table
                size="small"
                rowKey={(e, i) => `e${i}`}
                pagination={{ pageSize: 20 }}
                style={{ marginTop: 8 }}
                dataSource={events}
                columns={eventColumns}
              />
            </>
          ) : (
            <Alert
              type="info"
              showIcon
              message="No event-level details returned by the API"
              description="The all-device-report only returns a count for this field on this tenant — no per-event array was present. The Recalculate button (right) re-runs the new stage-based algorithm and produces detailed events."
            />
          )}
        </div>
      ),
    });
  };

  const showRejectedModal = (row, result) => {
    setModal({
      open: true,
      title: `Rejected Candidates — ${row.assetCode || row.altName || row.deviceId}`,
      content: (
        <Table
          size="small"
          rowKey={(e, i) => `${e.start_ts_ms || i}`}
          pagination={{ pageSize: 20 }}
          dataSource={result?.rejected || []}
          columns={[
            { title: 'Start (IST)', dataIndex: 'start_ist' },
            { title: 'End (IST)',   dataIndex: 'end_ist' },
            { title: 'Duration (s)', dataIndex: 'duration_sec', render: (v) => (v != null ? Number(v).toFixed(1) : '-') },
            { title: 'Peak kmph',   dataIndex: 'peak_speed_kmph', render: (v) => (v != null ? Number(v).toFixed(1) : '-') },
            { title: 'Reason',      dataIndex: 'reject_reason' },
          ]}
        />
      ),
    });
  };

  // ---- Columns ----------------------------------------------------------
  const columns = [
    {
      title: 'Asset Code',
      key: 'assetCode',
      fixed: 'left',
      width: 160,
      render: (_, r) => (
        <div>
          <div>{r.assetCode || '-'}</div>
          {r.altName ? <Text type="secondary" style={{ fontSize: 11 }}>{r.altName}</Text> : null}
        </div>
      ),
    },
    { title: 'Type',        key: 'type',    render: (_, r) => r.assetType    || '-' },
    { title: 'Sub Type',    key: 'subType', render: (_, r) => r.assetSubType || '-' },
    { title: 'Model',       key: 'model',   render: (_, r) => r.assetModel   || '-' },
    { title: 'Location',    key: 'loc',     render: (_, r) => r.location     || '-' },
    { title: 'Sub-Location (Client)', key: 'subLoc', render: (_, r) => r.subLocation || '-' },
    {
      title: (
        <Tooltip title="Sum of overspeed events across the date range, from AssetPro's daily all-device-report. Click to see details.">
          Existing Overspeed <Icon type="info-circle" />
        </Tooltip>
      ),
      key: 'existingOverspeed',
      render: (_, r) => {
        if (r.existingOverspeed === undefined) return <Text type="secondary">—</Text>;
        return (
          <Button
            size="small"
            type={r.existingOverspeed > 0 ? 'primary' : 'default'}
            onClick={() => showExistingEventsModal(r, 'overspeed')}
          >
            {r.existingOverspeed}
          </Button>
        );
      },
    },
    {
      title: (
        <Tooltip title="Existing harsh accel / brake / corner counters from the all-device-report, summed across the range. Click a tag for details.">
          Existing Green Driving <Icon type="info-circle" />
        </Tooltip>
      ),
      key: 'existingGreen',
      render: (_, r) => {
        const { existingHarshAccel: a, existingHarshBrake: b, existingHarshCorner: c } = r;
        if (a === undefined && b === undefined && c === undefined) return <Text type="secondary">—</Text>;
        const chip = (label, n, kind) => (
          <Button
            size="small"
            style={{ marginRight: 4, padding: '0 8px', height: 22 }}
            onClick={() => showExistingEventsModal(r, kind)}
          >
            {label} {n ?? 0}
          </Button>
        );
        return (
          <span>
            {chip('A', a, 'accel')}
            {chip('B', b, 'brake')}
            {chip('C', c, 'corner')}
          </span>
        );
      },
    },
    {
      title: 'New Overspeed (stage-based)',
      key: 'newOverspeed',
      render: (_, r) => {
        const s = recomputeState[rowKeyFor(r)];
        if (!s) return <Text type="secondary">Not run</Text>;
        if (s.status === 'running') return <Tag color="orange">Running…</Tag>;
        if (s.status === 'error') return <Tooltip title={s.error}><Tag color="red">Error</Tag></Tooltip>;
        const count = s.result?.events?.length ?? 0;
        return (
          <Button size="small" type={count > 0 ? 'primary' : 'default'} onClick={() => showEventsModal(r, s.result, s.rows)}>
            {count} event{count === 1 ? '' : 's'}
          </Button>
        );
      },
    },
    {
      title: 'Rejected',
      key: 'rejected',
      render: (_, r) => {
        const s = recomputeState[rowKeyFor(r)];
        if (!s || s.status !== 'done') return <Text type="secondary">—</Text>;
        const count = s.result?.rejected?.length ?? 0;
        return (
          <Badge
            count={count}
            style={{ backgroundColor: count ? '#faad14' : '#d9d9d9', cursor: count ? 'pointer' : 'default' }}
            onClick={count ? () => showRejectedModal(r, s.result) : undefined}
            showZero
          />
        );
      },
    },
    {
      title: 'Stages (rows → … → confirmed)',
      key: 'stages',
      render: (_, r) => {
        const s = recomputeState[rowKeyFor(r)];
        if (!s || s.status !== 'done') return <Text type="secondary">—</Text>;
        return renderStageCounts(s.result?.stage_counts);
      },
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 140,
      render: (_, r) => {
        const s = recomputeState[rowKeyFor(r)];
        const disabled = !r.deviceId || !r.assetId || s?.status === 'running';
        const tip = !r.deviceId || !r.assetId
          ? 'Row missing device id or asset id — recompute needs both to fetch raw telemetry'
          : 'Re-run overspeed detection using the new stage-based algorithm';
        return (
          <Tooltip title={tip}>
            <Button
              size="small"
              icon="reload"
              loading={s?.status === 'running'}
              disabled={disabled}
              onClick={() => runRecompute(r)}
            >
              Recalculate
            </Button>
          </Tooltip>
        );
      },
    },
  ];

  // ---- Render -----------------------------------------------------------
  return (
    <Content style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 4 }}>Overspeeding & Green Driving Events</Title>
      <Text type="secondary">
        Compare AssetPro's existing overspeed counts against the new stage-based detection algorithm
        over a date range. Raw IoT data is processed by the local <code>overspeed-service</code>.
      </Text>

      <Card style={{ marginTop: 16 }} bodyStyle={{ padding: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={8}>
            <label style={{ display: 'block', marginBottom: 8 }}>Date Range:</label>
            <RangePicker
              style={{ width: '100%' }}
              value={dateRange}
              onChange={setDateRange}
              disabledDate={(d) => d && d > moment().endOf('day')}
            />
          </Col>
          <Col span={4}>
            <label style={{ display: 'block', marginBottom: 8 }}>Speed Limit (kmph):</label>
            <InputNumber style={{ width: '100%' }} min={5} max={120} value={speedLimitKmph} onChange={setSpeedLimitKmph} />
          </Col>
          <Col span={4}>
            <label style={{ display: 'block', marginBottom: 8 }}>Peak Margin (%):</label>
            <InputNumber style={{ width: '100%' }} min={0} max={50} value={peakMarginPct} onChange={setPeakMarginPct} />
          </Col>
          <Col span={8} style={{ textAlign: 'right' }}>
            <Button type="primary" icon="search" onClick={handleLoad} loading={loading} style={{ marginTop: 30 }}>
              Load Report
            </Button>
          </Col>
        </Row>

        <Divider style={{ margin: '16px 0' }} />

        <Row gutter={16}>
          <Col span={8}>
            <label style={{ display: 'block', marginBottom: 8 }}>Asset Code:</label>
            <Select
              style={{ width: '100%' }}
              placeholder="All Asset Codes"
              allowClear
              showSearch
              value={selectedAssetCode}
              onChange={setSelectedAssetCode}
              filterOption={(input, option) => {
                const txt = String(option.props.children || option.children || '').toLowerCase();
                return txt.includes(String(input || '').toLowerCase());
              }}
            >
              {assetCodes.map((c) => <Option key={c} value={c}>{c}</Option>)}
            </Select>
          </Col>
          <Col span={8}>
            <label style={{ display: 'block', marginBottom: 8 }}>Asset Type:</label>
            <Select
              style={{ width: '100%' }}
              placeholder="All Asset Types"
              allowClear
              value={selectedAssetType}
              onChange={setSelectedAssetType}
            >
              {assetTypes.map((v) => <Option key={v} value={v}>{v}</Option>)}
            </Select>
          </Col>
          <Col span={8}>
            <label style={{ display: 'block', marginBottom: 8 }}>Asset Sub Type:</label>
            <Select
              style={{ width: '100%' }}
              placeholder="All Asset Sub Types"
              allowClear
              value={selectedAssetSubType}
              onChange={(v) => { setSelectedAssetSubType(v); setSelectedAssetModel(null); }}
            >
              {assetSubTypes.map((v) => <Option key={v} value={v}>{v}</Option>)}
            </Select>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={8}>
            <label style={{ display: 'block', marginBottom: 8 }}>Asset Model:</label>
            <Select
              style={{ width: '100%' }}
              placeholder="All Asset Models"
              allowClear
              value={selectedAssetModel}
              onChange={setSelectedAssetModel}
            >
              {assetModels.map((v) => <Option key={v} value={v}>{v}</Option>)}
            </Select>
          </Col>
          <Col span={8}>
            <label style={{ display: 'block', marginBottom: 8 }}>Location:</label>
            <Select
              style={{ width: '100%' }}
              placeholder="All Locations"
              allowClear
              value={selectedLocation}
              onChange={setSelectedLocation}
            >
              {locations.map((v) => <Option key={v} value={v}>{v}</Option>)}
            </Select>
          </Col>
          <Col span={8}>
            <label style={{ display: 'block', marginBottom: 8 }}>Sub-Location (Client):</label>
            <Select
              style={{ width: '100%' }}
              placeholder="All Sub Locations"
              allowClear
              value={selectedSubLocation}
              onChange={setSelectedSubLocation}
            >
              {subLocations.map((v) => <Option key={v} value={v}>{v}</Option>)}
            </Select>
          </Col>
        </Row>

        <Row style={{ marginTop: 16 }}>
          <Col>
            <Button onClick={handleReset} style={{ marginRight: 8 }}>Reset Filters</Button>
            <Button
              type="primary"
              icon="thunderbolt"
              onClick={runBulkRecompute}
              disabled={selectedRowKeys.length === 0}
            >
              Recalculate Selected ({selectedRowKeys.length})
            </Button>
          </Col>
        </Row>
      </Card>

      {loadInfo && !loadError && (
        <Alert style={{ marginTop: 16 }} type="info" showIcon message={loadInfo} />
      )}
      {loadError && (
        <Alert
          style={{ marginTop: 16 }}
          type="error"
          showIcon
          message="Failed to load report"
          description={loadError}
        />
      )}

      <Card style={{ marginTop: 16 }} bodyStyle={{ padding: 0 }}>
        <Table
          size="small"
          loading={loading}
          dataSource={filteredRows}
          rowKey={(r) => rowKeyFor(r)}
          columns={columns}
          scroll={{ x: 1500 }}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            getCheckboxProps: (r) => ({
              disabled: !r.deviceId || !r.assetId,
            }),
          }}
        />
      </Card>

      <Modal
        visible={modal.open}
        title={modal.title}
        onCancel={() => setModal({ open: false, title: '', content: null })}
        footer={null}
        width={900}
        destroyOnClose
      >
        {modal.content}
      </Modal>

      <OverspeedEventMapModal
        visible={mapModal.open}
        rows={mapModal.rows}
        event={mapModal.event}
        speedLimitKmph={mapModal.speedLimit}
        onClose={() => setMapModal({ open: false, rows: [], event: null, speedLimit: null })}
      />
    </Content>
  );
};

export default OverspeedGreenDrivingReport;
